import { useState } from 'react'
import { useQuery, useMutation} from '@tanstack/react-query'
import { 
    startNewAuth, verifyUserOTP, getAuthUser, updateUserData,
    createNewCommunity, fetchCommunity, updateCommunity, 
    signOut } from '../api/crud'
import { CommunitySchema, AccountSchema, AuthSchema } from "../data/types";
import { useAuthState } from '../contexts/authcontext'
import {
    canRestoreVerifiedSession,
    clearAuthVerified,
    getRestoreUsername,
    isRestoredSessionVerified,
    persistAuthTokens,
    persistVerifiedSession,
    readAuthSnapshot,
    readAuthVerified,
} from '../utils/authSession'


const staleTime = 1000
const isEnabled = true


// get user details from access token detail
export const useLoadAuthUser = () => {
    const { authUser, updateAuthUser, updateLogin, updateVerified } = useAuthState()
    const [hasRestoredSession, updateHasRestoredSession] = useState(false)

    const loadAuthMutation = useMutation({ 
        mutationFn: async (token_username: string) => {
            console.log("get auth token user")
            const res = await getAuthUser({token_username})
            if (res && Object.keys(res).length !== 0 && updateAuthUser && updateLogin && updateVerified){
                const locallyVerified = readAuthVerified()
                const isVerified = isRestoredSessionVerified(res.auth_status, locallyVerified)
                updateAuthUser(res)
                updateLogin(true)
                updateVerified(isVerified)
                if (isVerified) {
                    persistVerifiedSession(res)
                }
            }
            return res
    }})


    const registerUserMutation = useMutation({ 
        mutationFn: async (user: AccountSchema) => {
            console.log("register user")
            const res = await startNewAuth(user)
            clearAuthVerified()
            if (res && Object.keys(res).length !== 0 && updateAuthUser && updateLogin && updateVerified){
                persistAuthTokens(res.login_token, res.refresh_token)
                updateAuthUser(res)
                updateLogin(true)
                updateVerified(!!res.auth_status)
                if (res.auth_status) {
                    persistVerifiedSession(res)
                }
            }
            return res
    }})


    const registerBusinessMutation = useMutation({ 
        mutationFn: async ({community, account}: {community: CommunitySchema, account: AccountSchema}) => {
            console.log("register business")
            const res = await createNewCommunity(community, account)
            if (res && Object.keys(res).length !== 0 && updateAuthUser && updateLogin && updateVerified){
                persistAuthTokens(res.login_token, res.refresh_token)
                updateAuthUser(res)
                updateLogin(true)
                if (res.auth_status){
                    persistVerifiedSession(res)
                    updateVerified(res.auth_status) 
                }
            }
            return res
    }})

    const verifyUserMutation = useMutation({ 
        mutationFn: async (otp: string) => {
            console.log("verify user")
            if (authUser?.username){const res = await verifyUserOTP(otp,authUser.username)
            if (res && Object.keys(res).length !== 0 && updateAuthUser && updateLogin && updateVerified){
                persistAuthTokens(res.login_token, res.refresh_token)
                updateAuthUser(res)
                updateLogin(true)
                if (res.auth_status){
                    persistVerifiedSession(res)
                    updateVerified(res.auth_status) 
                    return true
                }
            }}
            return false
    }})


    const updateProfileMutation = useMutation({
        mutationFn: async ({username, accountUpdates}: {username: string, accountUpdates: AccountSchema}) => {
            return await updateUserData(communityId, username, accountUpdates)
        },
        onSuccess: (data: AccountSchema | null) => {
            if (data && Object.keys(data).length !== 0 && updateAuthUser && updateLogin && updateVerified){
                updateAuthUser(data)
                updateLogin(true)
                if (data.auth_status){
                    updateVerified(data.auth_status) 
                }
            }
        }
    })

    const updateAccountProfile = async (username: string, accountUpdates: AccountSchema) => {
        try {
            const result = await updateProfileMutation.mutateAsync({username, accountUpdates})
            return result ? true : false
        } catch {
            console.log("updateAccountProfile error")
            return false
        }
    }

    /** Restores in-memory auth from the stored JWT; waits until the API call settles. */
    const loadAuthUser = async () => {
        try {
            const access_token = localStorage.getItem('access_token') || ''
            const snapshot = readAuthSnapshot()
            const token_username = getRestoreUsername(access_token)
            const canRestore = canRestoreVerifiedSession(token_username)
            if (canRestore && updateLogin && updateVerified) {
                if (snapshot && updateAuthUser) {
                    updateAuthUser(snapshot as AuthSchema)
                } else if (updateAuthUser) {
                    updateAuthUser({ username: token_username, token_username, auth_status: true } as unknown as AuthSchema)
                }
                updateLogin(true)
                updateVerified(true)
            }
            if (token_username) {
                await loadAuthMutation.mutateAsync(token_username)
            }
            return true
        } catch {
            return false
        } finally {
            updateHasRestoredSession(true)
        }
    }

    const registerUser = (user: AccountSchema) => {
        registerUserMutation.mutate(user)
        return registerUserMutation?.data ? true : false
    }

    const registerBusiness = (community: CommunitySchema, account: AccountSchema) => {
        registerBusinessMutation.mutate({community, account})
        return registerBusinessMutation?.data ? true : false
    }

    const verifyUser = (otp: string) => {
        verifyUserMutation.mutate(otp)
        return verifyUserMutation?.data ? true : false
    } 

    const signOutUser = () => {
        console.log("signout")
        signOut()
        if (updateAuthUser && updateLogin && updateVerified){
            updateAuthUser({} as AuthSchema)
            updateLogin(false)
            updateVerified(false)
        }
    }

    var communityId = (authUser && authUser.primary_community) ? authUser.primary_community : ""
    var { isLoadingCommunity } = useLoadCommunity(communityId)

    const isLoading = (
        loadAuthMutation.isPending || 
        registerUserMutation.isPending || 
        registerBusinessMutation.isPending || 
        verifyUserMutation.isPending || 
        updateProfileMutation.isPending || 
        isLoadingCommunity
    );

    return { loadAuthUser, registerUser, registerBusiness, verifyUser, signOutUser, isLoading, isRestoringSession: !hasRestoredSession, updateAccountProfile }
}


export const useLoadCommunity = (communityId: string) => {
    let { updatePrimaryCommunity } = useAuthState()
    const {data: primaryCommunity, refetch: reloadCommunity, isLoading: isLoadingCommunity} = useQuery({ 
        queryKey: ['community', communityId], 
        queryFn: async () => {
            if (communityId){
                console.log("load community")
                const res = await fetchCommunity(communityId)
                if (res && updatePrimaryCommunity !== undefined){
                    updatePrimaryCommunity(res)
                }
                return res
            }
            return {}
        },
        staleTime,
        enabled: isEnabled
    })


    const updateCommunityMutation = useMutation({ 
        mutationFn: async ({communityId, communityUpdates}: {communityId: string, communityUpdates: CommunitySchema}) => {
            console.log("update community")
            const res = await updateCommunity(communityId, communityUpdates)
            reloadCommunity()
            return res
    }})

    const updateCommunityDetails = (communityId: string, communityUpdates: CommunitySchema) => {
        updateCommunityMutation.mutate({communityId, communityUpdates})
        return updateCommunityMutation?.data ? true : false
    } 

    const isUpdateSuccess = updateCommunityMutation.isSuccess
    const isUpdateError = updateCommunityMutation.isError
    const isUpdatePending = updateCommunityMutation.isPending

    return {primaryCommunity, isLoadingCommunity, isUpdatePending, isUpdateSuccess, isUpdateError, updateCommunityDetails, reloadCommunity}
}



    // const updateProfile = async (user_data: AccountSchema) => {
    //     const { authUser, updateAuthUser, updateVerified } = useAuthState()
    //     if (authUser && authUser.hasOwnProperty('phone')){
    //         let user
    //         if (user_data && authUser){
    //             user = await updateUserData(authUser, user_data)
    //         }
    //         if (user && Object.keys(user).length !== 0){
    //             updateAuthUser(user)
    //             if (user.auth_status){
    //                 // + if auth_user has verified attribute
    //                 updateVerified(user.auth_status) 
    //             }
    //             return true
    //         }
    //     }
    //     return false
    // }



    // // initiate user auth
    // const registerUser = async (user: AccountSchema) => {
    //     let loginType = user.phone ? 'phone' : 'email'
    //     localStorage.setItem('loginType', loginType)
    //     updateLoginType(loginType)

    //     var auth_user = await startNewAuth(user)
    //     // console.log("registerUser auth_user",auth_user)
    //     if (auth_user && auth_user.login_token && auth_user.refresh_token){
    //         updateAuthUser(auth_user)
    //         updateLogin(true)
    //         if (auth_user.auth_status){
    //             // + if auth_user has verified attribute
    //             updateVerified(auth_user.auth_status) 
    //         }
    //         localStorage.setItem('access_token', auth_user.login_token)
    //         localStorage.setItem('refresh_token', auth_user.refresh_token)
    //         return true
    //     }
    //     return false
    // }


    // const registerBusiness = async (community: CommunitySchema, account: AccountSchema) => {
    //     var auth_user = await createNewCommunity(community, account)
    //     if (auth_user && auth_user.login_token && auth_user.refresh_token){
    //         updateAuthUser(auth_user)
    //         updateLogin(true)
    //         if (auth_user.auth_status){
    //             updateVerified(auth_user.auth_status) 
    //         }
    //         localStorage.setItem('access_token', auth_user.login_token)
    //         localStorage.setItem('refresh_token', auth_user.refresh_token)
    //         var communityId = (auth_user && auth_user.primary_community) ? auth_user.primary_community : ""
    //         if (communityId){
    //             loadPrimaryCommunity(communityId)
    //         }
    //         return true
    //     }
    //     return false
    // }


    // const verifyUser = async (otp: string) => {
    //     let username = ""
    //     if (authUser && authUser?.username){
    //         username = authUser.username
    //     }
    //     let auth_user = await verifyUserOTP(otp, username)
    //     if (auth_user && auth_user?.auth_status && auth_user?.login_token){
    //         updateAuthUser(auth_user)
    //         updateLogin(true)
    //         if (auth_user?.auth_status){
    //             updateVerified(auth_user.auth_status)
    //             return true
    //         }
    //     }
    //     return false
    // }


    // const loadPrimaryCommunity = async (communityId: string) => {
    //     var communityDetails = await fetchCommunity(communityId)
    //     if (communityDetails){
    //         updatePrimaryCommunity(communityDetails)
    //     }
    // }


    // const handleUpdateLoginType = (loginType: string) => {
    //     updateLoginType(loginType ? loginType : 'phone')
    //     localStorage.setItem('loginType', loginType)
    // }



